import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BuildIcon from '@mui/icons-material/Build';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import useFreaksT from './useFreaksT';
import { actionSx, barSx, FREAKS_GOLD, FREAKS_RED, freaksSx as s, tonalSx } from './freaksStyles';

const fullWidth = { width: '100%', mt: 1 };

// The activity's business (garage, bait shop, …) earning cash per hour, up to a few hours' worth.
// Charisma raises the income; higher base levels unlock at higher freak levels. The base wears out
// (condition) and earns less below half condition until it's repaired.
export default function FreaksBase({ base, cash, disabled, onCollect, onUpgrade, onRepair }) {
  const { t } = useFreaksT();
  const built = base.level > 0;
  return (
    <Box sx={s.card} data-testid="freaks-base">
      <Box sx={{ ...s.row, gap: 1.5 }}>
        <StorefrontIcon sx={{ fontSize: 36, color: built ? DEFAULT_THEME_COLOR : '#888' }}/>
        <Box sx={s.grow}>
          <Typography sx={{ ...s.bold, fontSize: 20 }}>{base.name}</Typography>
          <Typography sx={s.muted}>
            {built ? t('theFreaks.base.level', { level: base.level, max: base.maxLevel }) : t('theFreaks.base.notBuilt')}
          </Typography>
        </Box>
      </Box>
      {built && base.wearing && base.condition !== undefined ? (
        <Box sx={{ mt: 1.5 }} data-testid="freaks-base-condition">
          <Typography sx={s.small}>🔧 {t('theFreaks.base.condition', { condition: base.condition })}</Typography>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, base.condition))}
            sx={{ ...barSx(base.condition < 50 ? FREAKS_RED : DEFAULT_THEME_COLOR), mt: 0.5 }}
          />
          {base.condition < 50
            ? <Typography sx={{ ...s.small, color: FREAKS_RED, mt: 0.5 }}>{t('theFreaks.base.worn')}</Typography>
            : null}
          {onRepair && base.repairPrice ? (
            <Button
              data-testid="freaks-base-repair"
              variant="outlined"
              startIcon={<BuildIcon/>}
              disabled={disabled || cash < base.repairPrice}
              onClick={onRepair}
              sx={{ ...fullWidth, minHeight: 44, borderRadius: 5, textTransform: 'none', color: DEFAULT_THEME_COLOR, borderColor: DEFAULT_THEME_COLOR }}
            >
              {t('theFreaks.base.repair', { price: base.repairPrice })}
            </Button>
          ) : null}
        </Box>
      ) : null}
      {built ? (
        <>
          <Typography sx={{ mt: 1.5 }}>💰 {t('theFreaks.base.income', { income: base.incomePerHour })}</Typography>
          <Typography sx={{ ...s.bold, color: FREAKS_GOLD, mt: 0.5 }} data-testid="freaks-base-waiting">
            {t('theFreaks.base.waiting', { cash: base.waiting, hours: base.storageHours })}
          </Typography>
          <Button
            data-testid="freaks-base-collect"
            disabled={disabled || base.waiting <= 0}
            onClick={onCollect}
            sx={{ ...actionSx(), ...fullWidth, mt: 1.5 }}
          >
            {t('theFreaks.base.collect')}
          </Button>
        </>
      ) : null}
      {base.upgradePrice !== null ? (
        <>
          {base.nextIncomePerHour !== null ? (
            <Typography sx={{ ...s.small, ...s.muted, mt: 1.5 }}>
              {t('theFreaks.base.nextIncome', { income: base.nextIncomePerHour })}
            </Typography>
          ) : null}
          {base.upgradeLocked ? (
            <Typography sx={{ ...s.small, mt: 1 }} data-testid="freaks-base-locked">
              🔒 {t('theFreaks.base.locked', { level: base.upgradeLevel })}
            </Typography>
          ) : null}
          <Button
            data-testid="freaks-base-upgrade"
            disabled={disabled || cash < base.upgradePrice || !!base.upgradeLocked}
            onClick={onUpgrade}
            sx={{ ...tonalSx, ...fullWidth }}
          >
            {built
              ? t('theFreaks.base.upgrade', { price: base.upgradePrice })
              : t('theFreaks.base.build', { price: base.upgradePrice })}
          </Button>
        </>
      ) : (
        <Typography sx={{ ...s.muted, mt: 1.5 }}>{t('theFreaks.base.maxed')}</Typography>
      )}
    </Box>
  );
}
